import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe, Eye, EyeOff, Phone, Mail } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, toggleLang } = useLanguage();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(
        authMode === 'email' ? email : null,
        password,
        authMode === 'phone' ? phone : null
      );
      const role = userData.role;
      if (role === 'admin') navigate('/admin');
      else if (role === 'lawyer') navigate('/lawyer');
      else navigate('/client');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col lg:flex-row" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0F172A]">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1547067592-463a85de7fd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMG1hcmJsZXxlbnwwfHx8fDE3NzU5NTcyNTB8MA&ixlib=rb-4.1.0&q=85)` }} />
        <div className="relative flex flex-col justify-center px-12 xl:px-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
              <img src="https://img1.wsimg.com/isteam/ip/e4bd2fa3-4e7e-4367-966e-734c45bd3de1/B679532D-818B-41E0-B271-6D14114F9E5C.png/:/rs=h:64,cg:true,m/qt=q:95" alt="Logo" className="h-12 w-auto" />
            </div>
          </div>
          <h2 className="text-2xl text-white font-heading mb-4">{t('hero_title')}</h2>
          <p className="text-slate-400 text-base leading-relaxed max-w-md">{t('hero_subtitle')}</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-md">
          <div className="flex justify-between items-center mb-6 sm:mb-8">
            <Link to="/" className="flex items-center gap-2 lg:hidden">
              <img src="https://img1.wsimg.com/isteam/ip/e4bd2fa3-4e7e-4367-966e-734c45bd3de1/B679532D-818B-41E0-B271-6D14114F9E5C.png/:/rs=h:64,cg:true,m/qt=q:95" alt="Logo" className="h-8 sm:h-10 w-auto" />
            </Link>
            <button onClick={toggleLang} className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A]" data-testid="login-lang-toggle">
              <Globe className="w-4 h-4" /> {lang === 'en' ? 'العربية' : 'English'}
            </button>
          </div>

          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading mb-2">{t('login')}</h1>
          <p className="text-sm text-[#475569] mb-6">{t('welcome_back')}</p>

          {/* Auth mode toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1 mb-6" data-testid="login-auth-mode-toggle">
            <button
              type="button"
              onClick={() => setAuthMode('email')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${authMode === 'email' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569]'}`}
              data-testid="login-mode-email"
            >
              <Mail className="w-4 h-4" /> {t('email')}
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('phone')}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${authMode === 'phone' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#475569]'}`}
              data-testid="login-mode-phone"
            >
              <Phone className="w-4 h-4" /> {t('phone')}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {authMode === 'email' ? (
              <div>
                <Label htmlFor="email" className="text-[#0F172A] font-medium text-sm">{t('email')}</Label>
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required={authMode === 'email'} className="mt-1 h-11 focus:ring-2 focus:ring-[#B48E4B]/50" placeholder="name@example.com" data-testid="login-email-input" />
              </div>
            ) : (
              <div>
                <Label htmlFor="phone" className="text-[#0F172A] font-medium text-sm">{t('phone')}</Label>
                <Input id="phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} required={authMode === 'phone'} className="mt-1 h-11 focus:ring-2 focus:ring-[#B48E4B]/50" placeholder="+962 7X XXX XXXX" dir="ltr" data-testid="login-phone-input" />
              </div>
            )}
            <div>
              <Label htmlFor="password" className="text-[#0F172A] font-medium text-sm">{t('password')}</Label>
              <div className="relative mt-1">
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required className="h-11 pe-10 focus:ring-2 focus:ring-[#B48E4B]/50" placeholder="••••••••" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#94A3B8] hover:text-[#475569]">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0F172A] text-white hover:bg-[#1E293B] text-sm" data-testid="login-submit-button">
              {loading ? '...' : t('sign_in')}
            </Button>
            <div className="text-end">
              <Link to="/forgot-password" className="text-xs text-[#B48E4B] hover:underline" data-testid="forgot-password-link">
                {lang === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot password?'}
              </Link>
            </div>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-4 bg-[#F8F9FA] text-[#94A3B8]">{t('or_continue_with')}</span></div>
          </div>

          <Button variant="outline" onClick={handleGoogleLogin} className="w-full h-11 border-slate-300 text-[#0F172A] hover:bg-slate-50 text-sm" data-testid="google-login-button">
            <svg className="w-4 h-4 me-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            {t('google_signin')}
          </Button>

          <p className="mt-5 text-center text-sm text-[#475569]">
            {t('no_account')}{' '}
            <Link to="/register" className="text-[#B48E4B] font-medium hover:underline" data-testid="goto-register-link">{t('register')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
